import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Tab, Tabs } from '@mui/material';
import { JournalFilters, JournalMeta, OptionField, OptionPick } from '../../types';
import { BORDER } from './chartKit';
import InactiveView from './InactiveView';
import PharmaciesView from './PharmaciesView';
import ProductView from './ProductView';
import StructureView from './StructureView';
import TimelineView from './TimelineView';

type View = 'timeline' | 'structure' | 'pharmacies' | 'product' | 'inactive';

const VIEWS: Array<{ value: View; label: string }> = [
  { value: 'timeline', label: 'Динамика' },
  { value: 'structure', label: 'Структура' },
  { value: 'pharmacies', label: 'Аптеки' },
  { value: 'product', label: 'Товар' },
  { value: 'inactive', label: 'Без записей' }
];

interface Props {
  meta: JournalMeta;
  filters: JournalFilters;
  /** Применённые фильтры одной строкой - ключ загрузок всех видов */
  paramsKey: string;
  /** Вкладка «Аналитика» открыта: на вкладке «Таблица» виды не загружаются */
  active: boolean;
  reloadToken: number;
  onPatch: (patch: Partial<JournalFilters>) => void;
  onPick: (field: OptionField, pick: OptionPick | null) => void;
}

/**
 * Аналитика журнала. Все виды строятся по фильтрам панели над вкладками.
 * Вид загружается при первом открытии и остаётся смонтированным: при возврате к нему без смены фильтров
 * данные не перезапрашиваются, скрытый вид запросов не шлёт.
 */
const AnalyticsPanel: React.FC<Props> = ({ meta, filters, paramsKey, active, reloadToken, onPatch, onPick }) => {
  const [view, setView] = useState<View>('timeline');
  const [visited, setVisited] = useState<View[]>(['timeline']);
  /** Товар вкладки «Товар», выбранный внутри аналитики (без фильтра журнала) */
  const [localProduct, setLocalProduct] = useState<OptionPick | null>(null);

  useEffect(() => {
    setVisited(prev => (prev.indexOf(view) === -1 ? [...prev, view] : prev));
  }, [view]);

  const openProduct = useCallback((pick: OptionPick) => {
    setLocalProduct(pick);
    setView('product');
  }, []);

  const pickProduct = useCallback((field: OptionField, pick: OptionPick | null) => {
    // фильтр по товару с вкладки «Товар»: локальный выбор больше не нужен, после снятия фильтра карточка останется на нём
    if (field === 'product' && pick) setLocalProduct(pick);
    onPick(field, pick);
  }, [onPick]);

  const globalProduct = filters.picks.product;
  const product = globalProduct || localProduct;
  const narrowed = meta.access.analyticsScope === 'assigned_pharmacies' && meta.scope === 'all';
  const mounted = (v: View) => v === view || visited.indexOf(v) !== -1;
  const pane = (v: View) => ({ display: v === view ? 'block' : 'none' });
  const isActive = (v: View) => active && v === view;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ borderBottom: `1px solid ${BORDER}` }}>
        <Tabs
          value={view} onChange={(_, v: View) => setView(v)} variant="scrollable" scrollButtons="auto"
          sx={{ minHeight: 38, '& .MuiTab-root': { minHeight: 38, py: 0.5, px: 1.75, textTransform: 'none', fontSize: '0.84rem', fontWeight: 600 } }}
        >
          {VIEWS.map(v => <Tab key={v.value} value={v.value} label={v.label} />)}
        </Tabs>
      </Box>

      {narrowed && (
        <Alert severity="info" icon={false} sx={{ py: 0, fontSize: '0.78rem' }}>
          Аналитика строится только по курируемым вами аптекам, таблица журнала - по всей сети.
        </Alert>
      )}

      {mounted('timeline') && (
        <Box sx={pane('timeline')}>
          <TimelineView filters={filters} paramsKey={paramsKey} active={isActive('timeline')} reloadToken={reloadToken} onPatch={onPatch} />
        </Box>
      )}
      {mounted('structure') && (
        <Box sx={pane('structure')}>
          <StructureView
            filters={filters} paramsKey={paramsKey} active={isActive('structure')} reloadToken={reloadToken}
            onPatch={onPatch} onPick={onPick} onProduct={openProduct}
          />
        </Box>
      )}
      {mounted('pharmacies') && (
        <Box sx={pane('pharmacies')}>
          <PharmaciesView filters={filters} paramsKey={paramsKey} active={isActive('pharmacies')} reloadToken={reloadToken} onPatch={onPatch} />
        </Box>
      )}
      {mounted('product') && (
        <Box sx={pane('product')}>
          <ProductView
            filters={filters} paramsKey={paramsKey} active={isActive('product')} reloadToken={reloadToken}
            pick={product} fromFilters={Boolean(globalProduct)} onLocalPick={setLocalProduct} onPick={pickProduct} onPatch={onPatch}
          />
        </Box>
      )}
      {mounted('inactive') && (
        <Box sx={pane('inactive')}>
          <InactiveView filters={filters} paramsKey={paramsKey} active={isActive('inactive')} reloadToken={reloadToken} />
        </Box>
      )}
    </Box>
  );
};

export default React.memo(AnalyticsPanel);
